import React, { type ComponentType, useCallback, useEffect, useMemo } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import {
	type EditorRemixLifecycleOwner,
	useEditorRemixLifecycleOwner,
} from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';
import type {
	EditorRemixLifecycleAdapter,
	EditorRemixLifecycleAdapterHostProps,
	EditorRemixMachineLifecycleSession,
} from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import {
	registerEditorInlineRovoEditLifecycleAdapter,
	registerEditorInlineRovoEditLifecycleOwner,
	observeEditorInlineRovoEditProducerAttempt,
	startEditorInlineRovoEditHandoffPresentation,
} from '../utils/editor-inline-rovo-edit-lifecycle';
import { registerEditorRemixMachineLifecycleOwnership } from '../utils/editor-remix-callback-registry';
import { getInlineRemixMetadata } from '../utils/get-inline-remix-metadata';

type EditorInlineRovoEditLifecycleHostProps = {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
	LifecycleAdapterHost: ComponentType<EditorRemixLifecycleAdapterHostProps>;
};

/** Keeps inline-edit lifecycle admission available while Remix presentation is idle. */
export const EditorInlineRovoEditLifecycleHost = ({
	api,
	LifecycleAdapterHost,
}: EditorInlineRovoEditLifecycleHostProps): React.JSX.Element => {
	const lifecycleOwner = useEditorRemixLifecycleOwner();
	const retain = lifecycleOwner?.retain;
	const forgetMachineAuthority = lifecycleOwner?.forgetMachineAuthority;
	// Register only stable owner actions so activity changes cannot tear down an admitted attempt.
	const lifecycleOwnerForRegistration = useMemo<EditorRemixLifecycleOwner | undefined>(() => {
		if (!retain || !forgetMachineAuthority) {
			return undefined;
		}
		return {
			active: undefined,
			forgetMachineAuthority,
			retain,
		};
	}, [forgetMachineAuthority, retain]);
	const onRegisterEditorRemixMachineLifecycleOwnership = useCallback(
		(session: EditorRemixMachineLifecycleSession) =>
			registerEditorRemixMachineLifecycleOwnership(api, session),
		[api],
	);
	const onRegisterEditorInlineRovoEditLifecycleAdapter = useCallback(
		(adapter: EditorRemixLifecycleAdapter) =>
			registerEditorInlineRovoEditLifecycleAdapter(api, adapter),
		[api],
	);
	const onStartInlineRovoEditHandoffPresentation = useCallback(
		(attemptId: string) => {
			if (!api?.core.actions.execute || !api.malleableUi?.commands.startRemixThinking) {
				return false;
			}
			if (!startEditorInlineRovoEditHandoffPresentation(api, attemptId)) {
				return false;
			}
			// Chat can already be open when the inline prompt submits. Promote the
			// toolbar's staged target before the prompt closes its editor experience.
			if (isExperimentEnabled('cc_maui_1293_chat_thinking_bar')) {
				api.aiExperience?.actions.commitInlineRovoStreaming?.();
			}
			api.core.actions.execute(
				api.malleableUi.commands.startRemixThinking({
					isUpdate: true,
					remixAttemptId: attemptId,
				}),
			);
			return true;
		},
		[api],
	);

	useEffect(() => {
		if (!lifecycleOwnerForRegistration) {
			return;
		}
		return registerEditorInlineRovoEditLifecycleOwner(api, lifecycleOwnerForRegistration);
	}, [api, lifecycleOwnerForRegistration]);

	useEffect(() => {
		return api?.aiExperience?.actions.registerInlineRovoEditAdmission?.((targetId, editorState) => {
			try {
				const admission = api.malleableUi?.actions.beginInlineRovoEdit?.(targetId) ?? {
					status: 'unavailable' as const,
				};
				if (
					!isExperimentEnabled('maui_remix_feature_area_by_type_exp_gate') ||
					!editorState ||
					!targetId
				) {
					return admission;
				}
				const remixMetadata = getInlineRemixMetadata(editorState, targetId);
				return { ...admission, ...(remixMetadata && { remixMetadata }) };
			} catch {
				// The bridge is optional; unavailable lifecycle infrastructure preserves legacy submission.
				return { status: 'unavailable' };
			}
		});
	}, [api]);

	useEffect(() => {
		return api?.aiExperience?.actions.registerInlineRovoEditProducerAttemptReporting?.(
			(observation) => observeEditorInlineRovoEditProducerAttempt(api, observation),
		);
	}, [api]);

	useEffect(() => {
		return api?.aiExperience?.actions.registerInlineRovoEditHandoffPresentation?.(
			onStartInlineRovoEditHandoffPresentation,
		);
	}, [api, onStartInlineRovoEditHandoffPresentation]);

	useEffect(() => {
		return api?.aiExperience?.actions.registerInlineRovoEditReporting?.(
			(attemptId, outcome) =>
				api.malleableUi?.actions.reportInlineRovoEdit?.(attemptId, outcome) ?? false,
		);
	}, [api]);

	return (
		<LifecycleAdapterHost
			onRegisterEditorRemixMachineLifecycleOwnership={
				onRegisterEditorRemixMachineLifecycleOwnership
			}
			onRegisterEditorInlineRovoEditLifecycleAdapter={
				onRegisterEditorInlineRovoEditLifecycleAdapter
			}
		/>
	);
};
